import { TrashIcon } from "@heroicons/react/24/solid";
import {
  Drawer,
  Progress,
  Text,
  Stack,
  useMantineTheme,
  Group,
  Loader,
  CheckIcon,
  Divider,
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import {
  Cancel,
  CancelOutlined,
  CancelPresentation,
  CancelRounded,
  CancelTwoTone,
  DeleteOutline,
} from "@mui/icons-material";
import { CircularProgress, colors } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { PubSubNotificationTypes } from "src/enums/Notifications";
import { useNotifications } from "src/hooks/useNotifications";
import { Documents } from "src/interfaces/Documents";
import { FileUploadStatus } from "src/interfaces/KnowledgeBase";
import { COLORS } from "src/utils/styles";

interface ExtendedFileUploadStatus extends FileUploadStatus {
  info?: string;
  processed_count?: number;
}

const { setLogLevel } = require("@azure/logger");

setLogLevel("verbose");

interface UploadFilesDrawer {
  files: FileUploadStatus[];
  visible?: boolean;
  onPressClose: () => void;
  location?: "bottom" | "right" | "left" | "top";
}

interface FileIcon {
  status: string | undefined;
}

const FileIcon = (props: FileIcon) => {
  const { status } = props;
  return (
    <div className="flex-1 max-w-[10%] overflow-hidden flex items-center justify-center">
      {status === "success" ? (
        <div
          className="bg-green-500 flex items-center justify-center rounded-full"
          style={{ width: "20px", height: "20px" }}
        >
          {/* Green background with set dimensions to create a perfect circle */}
          <CheckIcon className="w-3 h-3 text-white fill-current" />{" "}
          {/* Icon is white */}
        </div>
      ) : status === "Deleted" ? (
        <div
          className=" bg-red-600 flex items-center justify-center rounded-full"
          style={{ width: "20px", height: "20px" }}
        >
          {/* Green background with set dimensions to create a perfect circle */}
          <TrashIcon className="w-3 h-3 text-white fill-current" />{" "}
          {/* Icon is white */}
        </div>
      ) : status === "error" ? (
        <div
          className=" flex items-center justify-center rounded-full"
          style={{ width: "20px", height: "20px" }}
        >
          {/* Green background with set dimensions to create a perfect circle */}
          <CancelRounded className="w-6 h-6 text-red-600 fill-current" />{" "}
          {/* Icon is white */}
        </div>
      ) : (
        <Loader size={20} />
      )}
    </div>
  );
};

export function KnowledgebaseFilesDrawer(props: UploadFilesDrawer) {
  const { files, onPressClose, location = "bottom" } = props;
  const [opened, { open, close }] = useDisclosure(false);
  const client = useNotifications()?.client;
  const isMatchces = useMediaQuery("(max-width: 992px)");
  const [list, setList] = useState<ExtendedFileUploadStatus[]>(files);
  const [initialized, setInitialized] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);

  const closeList = useCallback(() => {
    close();
    // onPressClose();
  }, [close]);

  // const wipeList = useCallback(() => {
  //   close()
  //   setList((prev) => [])

  // }, [])

  useEffect(() => {
    console.log("KnowledgebaseFilesDrawer: useEffect triggered. Client:", !!client, "List length:", list?.length);

    if (list?.length > 0 && client) {
      console.log("KnowledgebaseFilesDrawer: Attempting to subscribe to PubSub.");

      const handleMessage = (data: any) => {
        console.log("KnowledgebaseFilesDrawer: Raw group-message received:", data);
        if (data.message.data) {
          console.log("Notification received content:", data.message.data);
          try {
            const message = JSON.parse(data.message.data.toString());
            console.log("Parsed PubSub message:", message);

            if (message.event_type === PubSubNotificationTypes.studentDataUpdate) {
              console.log("Processing student_data_update");
              // ... logic ...
              setList((prevFiles) => {
                return prevFiles.map((file) => ({ ...file, info: message.info }));
              });
            } else if (message.event_type === PubSubNotificationTypes.fileUpload) {
              console.log("Processing file_upload");
              // ... logic ...
              // Simplified for brevity in logs, keeping existing logic structure
              setList((prevFiles) => {
                /* ... existing logic ... */
                // Re-inserting the previous logic exactly to ensure no regression
                const newFiles = prevFiles.map((file) => {
                  const fileName = typeof file.file === 'string' ? file.file : file.file.name;
                  if (fileName === message.file_name) {
                    return { ...file, status: message.status };
                  }
                  return file;
                });
                if (message.status == "Completed" || message.status == "Upload Failed") {
                  setProcessedCount((prev) => prev + 1)
                }
                if (newFiles.every((file) => file.status === "Completed" || file.status === "Upload Failed")) {
                  if (onPressClose) onPressClose();
                }
                return newFiles;
              });
            } else if (message.event_type === PubSubNotificationTypes.fileDeletion) {
              console.log("Processing file_delete");
              // ... logic ...
              setList((prevFiles) => {
                const newFiles = prevFiles.map((file) => {
                  const fileName = typeof file.file === 'string' ? file.file : file.file.name;
                  if (fileName === message.file_name) {
                    return { ...file, status: message.status };
                  }
                  return file;
                });
                if (message.status == "Deleted") {
                  setProcessedCount((prev) => prev + 1)
                }
                if (newFiles.every((file) => file.status === "Deleted")) {
                  if (onPressClose) {
                    onPressClose();
                    setTimeout(closeList, 2000)
                  }
                }
                return newFiles;
              });
            } else {
              console.log("Unhandled event type:", message.event_type);
            }
          } catch (err) {
            console.error("Error parsing PubSub message:", err);
          }
        }
      };

      client.on("group-message", handleMessage);
      console.log("Subscribed listener to 'group-message'");

      return () => {
        console.log("Unsubscribing from 'group-message'");
        client.off("group-message", handleMessage);
      };
    } else {
      console.log("KnowledgebaseFilesDrawer: Conditions not met for subscription. List empty or client missing.");
    }
  }, [client, list?.length > 0, closeList, onPressClose]);

  useEffect(() => {
    setList(files);
  }, [files]);

  useEffect(() => {
    if (list?.length > 0) {
      open();
      // listenForQueueFiles();
      // setInitialized(true);
    } else {
      closeList();
    }
  }, [list]);

  return (
    <>
      <Drawer
        opened={opened}
        transitionProps={{
          transition: "rotate-left",
          duration: 150,
          timingFunction: "linear",
        }}
        withCloseButton={true}
        onClose={() => {
          closeList();
          onPressClose();
        }}
        title={"Process Knowledgebase Files"}
        padding="lg"
        position={location} // Use 'location' to handle the position
        withOverlay={false}
        lockScroll={false}
        styles={(theme) => ({
          inner: {
            // Remove maxHeight to allow the drawer to size to its content
            // maxWidth: isMatchces ? "100%" : 350,
            // Align the drawer to the right side when the location is 'bottom'
            ...(location === "bottom" && {
              right: 0,
              left: "auto", // Ensure it doesn't try to center or stick to the left
              top: "auto", // Ensure it's at the bottom
            }),
            // Use this style when the location is 'right'
            ...(location === "right" && {
              right: 0,
              bottom: 0,
            }),
          },
          body: {
            marginTop: 10,
          },
          title: {
            fontStyle: "italic",
          },
          header: {
            backgroundColor: theme.colors.blue[0],
            borderRadius: "16px 16px 0 0",
            position: "sticky",
            height: 10,
            // The position property is no longer necessary here
          },
        })}
      >
        {list.map((file, index) => (
          <div key={index}>
            <div className="w-full py-2">
              <div className="flex flex-row justify-between">
                <div className="flex flex-col">
                  <Text style={{ fontSize: 14 }}>
                    {typeof file.file === "string" ? file.file : file.file.name}
                  </Text>
                  <Text style={{ fontSize: 10, color: colors.blue[300] }}>
                    {file.info ? file.info : file.status}
                  </Text>
                </div>
                <FileIcon status={file?.status} />
              </div>
            </div>
            <Divider w={"100%"} />
          </div>
        ))}
      </Drawer>
    </>
  );
}

export default UploadFilesDrawer;
